import Feature from "ol/Feature"
import GeoJSON from "ol/format/GeoJSON"
import BaseLayer from "ol/layer/Base"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import { Fill, Stroke, Style } from "ol/style"
import CircleStyle from "ol/style/Circle"
import { PaintbrushGeoJSONLayer } from "../state"
import { Geometry } from "geojson"

const FORMAT = new GeoJSON()

function firstCoordinate(geometry: Geometry | null | undefined): number[] | null {
  if (!geometry) return null
  switch (geometry.type) {
    case "Point":
      return geometry.coordinates as number[]
    case "MultiPoint":
    case "LineString":
      return (geometry.coordinates?.[0] as number[]) ?? null
    case "MultiLineString":
    case "Polygon":
      return (geometry.coordinates?.[0]?.[0] as number[]) ?? null
    case "MultiPolygon":
      return (geometry.coordinates?.[0]?.[0]?.[0] as number[]) ?? null
    case "GeometryCollection":
      return firstCoordinate(geometry.geometries?.[0])
    default:
      return null
  }
}

function detectDataProjection(geojson: any): "EPSG:4326" | "EPSG:3857" {
  const features = Array.isArray(geojson?.features) ? geojson.features : []
  const coord =
    firstCoordinate(features[0]?.geometry as Geometry | undefined) ??
    firstCoordinate(geojson?.geometry as Geometry | undefined)

  if (!coord || coord.length < 2) return "EPSG:4326"
  const [x, y] = coord
  const looksLikeLonLat = Math.abs(x) <= 180 && Math.abs(y) <= 90
  return looksLikeLonLat ? "EPSG:4326" : "EPSG:3857"
}

function normalizeHex(color: string | undefined): string {
  if (!color || typeof color !== "string") {
    return "#ff7f50"
  }
  const t = color.trim()
  if (t.startsWith("#") && t.length >= 7) {
    return t.slice(0, 7)
  }
  if (/^[0-9a-fA-F]{6}$/.test(t)) {
    return `#${t}`
  }
  return "#ff7f50"
}

function hexToRgba(hex: string, alpha: number): string {
  const h = normalizeHex(hex)
  const r = parseInt(h.slice(1, 3), 16)
  const g = parseInt(h.slice(3, 5), 16)
  const b = parseInt(h.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function featureOpacity(feature: Feature, layerOpacity: number): number {
  const slider = Math.min(1, Math.max(0, layerOpacity))
  const stored = feature.get("opacity")
  const base = typeof stored === "number" && !Number.isNaN(stored)
    ? Math.min(1, Math.max(0, stored))
    : 1
  return slider * base
}

function featureStrokeColor(feature: Feature): string {
  const raw = (feature.get("color") || feature.get("strokeColor")) as string | undefined
  return normalizeHex(raw)
}

const styleCache: Record<string, Style> = {}

function normalizeClassName(value: unknown): string {
  return String(value ?? "").trim().toLowerCase()
}

function paintbrushFeatureStyle(feature: Feature, layerOpacity: number, hiddenClassNames: string[]): Style | undefined {
  const featureClassName = normalizeClassName(feature.get("className"))
  if (featureClassName && hiddenClassNames.includes(featureClassName)) {
    return undefined
  }

  const color = featureStrokeColor(feature)
  const alpha = featureOpacity(feature, layerOpacity)
  const geom = feature.getGeometry()
  const gt = geom?.getType()

  if (gt === "Point" || gt === "MultiPoint") {
    const key = `pt-${color}-${alpha}`
    if (!styleCache[key]) {
      styleCache[key] = new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: hexToRgba(color, alpha) }),
          stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 1.5 }),
        }),
      })
    }
    return styleCache[key]
  }

  const isPolygon = gt === "Polygon" || gt === "MultiPolygon"
  const isLine = gt === "LineString" || gt === "MultiLineString"
  const key = `${isPolygon ? "poly" : isLine ? "line" : "geom"}-${color}-${alpha}`
  if (!styleCache[key]) {
    const stroke = new Stroke({
      color: hexToRgba(color, alpha),
      width: 2,
      lineCap: "round",
      lineJoin: "round",
    })
    if (isPolygon) {
      styleCache[key] = new Style({
        stroke,
        fill: new Fill({ color: hexToRgba(color, alpha) }),
      })
    } else {
      styleCache[key] = new Style({ stroke })
    }
  }
  return styleCache[key]
}

export function reifyPaintbrushGeoJSONLayer(
  layer: PaintbrushGeoJSONLayer,
  existingLayer: BaseLayer | null
): VectorLayer<VectorSource> {
  const hiddenClassNames = (layer.hiddenClassNames || []).map(normalizeClassName).filter(Boolean)
  if (
    existingLayer instanceof VectorLayer &&
    existingLayer.get("annotationId") === layer.annotationId &&
    existingLayer.get("geojsonUrl") === layer.geojsonUrl
  ) {
    existingLayer.set("isPaintbrushGeoJSONLayer", true)
    existingLayer.setStyle((feature) => paintbrushFeatureStyle(feature as Feature, layer.opacity, hiddenClassNames))
    existingLayer.changed()
    existingLayer.setOpacity(1)
    return existingLayer
  }

  const source = new VectorSource({
    url: layer.geojsonUrl,
    format: FORMAT,
    loader: (extent, resolution, projection, success, failure) => {
      fetch(layer.geojsonUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load GeoJSON (${response.status})`)
          return response.json()
        })
        .then((geojson) => {
          const dataProjection = detectDataProjection(geojson)
          const features = FORMAT.readFeatures(geojson, {
            dataProjection,
            featureProjection: "EPSG:3857",
          })
          source.clear()
          source.addFeatures(features)
          success?.(features)
        })
        .catch((error) => {
          console.warn(`Unable to load Paintbrush GeoJSON layer: ${layer.name}`, error)
          failure?.()
        })
    },
  })

  const vectorLayer = new VectorLayer({
    source,
    style: (feature) => paintbrushFeatureStyle(feature as Feature, layer.opacity, hiddenClassNames),
    opacity: 1,
  })
  vectorLayer.set("annotationId", layer.annotationId)
  vectorLayer.set("geojsonUrl", layer.geojsonUrl)
  vectorLayer.set("isPaintbrushGeoJSONLayer", true)
  return vectorLayer
}
